/**
 * Ежегодный вопрос «что менялось» раздела «Экология» (ecology-survey, макет клиента 16.09, решение
 * 16.09 в ТЗ): через период из настроек раздела после опроса по площадке кабинет спрашивает, что
 * менялось. «Ничего не менялось» откладывает вопрос ещё на период; «Были изменения» — модалка с
 * видами изменений и площадками: документы площадок, которые обновляются после этих изменений,
 * получают «Требует актуализации». Сообщить об изменениях можно в любой момент — кнопка «Были
 * изменения» на обзоре.
 *
 * `useEcoChangeCheck` держит модалку, запросы и сообщение об итоге; `ChangeCheckBanner` — плашка
 * для площадок, по которым пора спросить.
 */
import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react'
import { Clock } from 'lucide-react'
import type {
  EcoChangeCheckInput,
  EcoOverview,
  EcoScopeApi,
  EcoSite,
  EcoUpdateTrigger,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import {
  ECO_UPDATE_TRIGGERS,
  ECO_UPDATE_TRIGGER_LABELS,
  formatEcoDate,
  toggleCanonical,
} from '@/lib/ecology'
import { pluralRu } from '@/lib/orders'
import { LabeledCheckbox } from '@/sout/labeled-checkbox'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Alert } from '@/design-system/components/feedback/alert'

const TEXTAREA_CLASS =
  'w-full rounded-8 border border-line-strong bg-white px-3 py-2 text-14 text-txt outline-none transition-colors placeholder:text-txt-tertiary hover:border-line-strong focus-visible:ring-2 focus-visible:ring-accent'

function ChangeCheckDialog({
  open,
  sites,
  defaultSiteIds,
  saving,
  error,
  onClose,
  onSubmit,
}: {
  open: boolean
  sites: EcoSite[]
  defaultSiteIds: string[]
  saving: boolean
  error: string | null
  onClose: () => void
  onSubmit: (input: EcoChangeCheckInput) => void
}) {
  const formId = useId()
  const [changes, setChanges] = useState<EcoUpdateTrigger[]>([])
  const [siteIds, setSiteIds] = useState<string[]>([])
  const [note, setNote] = useState('')
  const [showErrors, setShowErrors] = useState(false)

  useEffect(() => {
    if (!open) return
    setChanges([])
    setSiteIds(defaultSiteIds)
    setNote('')
    setShowErrors(false)
  }, [open, defaultSiteIds])

  const changesError = showErrors && changes.length === 0 ? 'Отметьте, что менялось' : undefined
  const sitesError = showErrors && siteIds.length === 0 ? 'Отметьте площадки' : undefined

  function submit(e: FormEvent) {
    e.preventDefault()
    if (saving) return
    if (changes.length === 0 || siteIds.length === 0) {
      setShowErrors(true)
      return
    }
    onSubmit({ changes, siteIds, note: note.trim() })
  }

  const title = 'Что менялось на предприятии?'
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !saving) onClose()
      }}
    >
      <DialogContent title={title} className="max-w-xl">
        <Card
          scrollable
          className="max-h-dialog"
          header={title}
          footer={
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="secondary" onClick={onClose} disabled={saving}>
                Отмена
              </Button>
              <Button type="submit" form={formId} disabled={saving}>
                {saving ? 'Сохранение…' : 'Показать, что обновить'}
              </Button>
            </div>
          }
        >
          <form id={formId} onSubmit={submit} noValidate>
            <p className="text-13 text-txt-secondary">
              Отметьте изменения — кабинет подсветит документы, которые нужно обновить.
            </p>
            {error ? (
              <Alert variant="danger" className="mt-3">
                {error}
              </Alert>
            ) : null}
            <div className="mt-4 flex flex-col gap-2.5" role="group" aria-label="Что менялось">
              {ECO_UPDATE_TRIGGERS.map((t) => (
                <LabeledCheckbox
                  key={t}
                  checked={changes.includes(t)}
                  onCheckedChange={(on) =>
                    setChanges(toggleCanonical(ECO_UPDATE_TRIGGERS, changes, t, on))
                  }
                >
                  {ECO_UPDATE_TRIGGER_LABELS[t]}
                </LabeledCheckbox>
              ))}
            </div>
            {changesError ? (
              <p role="alert" className="mt-1.5 text-12 text-red">
                {changesError}
              </p>
            ) : null}
            <Field label="Что именно и как давно">
              {(props) => (
                <textarea
                  {...props}
                  rows={2}
                  className={TEXTAREA_CLASS}
                  value={note}
                  placeholder="Например: в 2026 году поставили новую котельную"
                  onChange={(e) => setNote(e.target.value)}
                />
              )}
            </Field>
            {sites.length > 1 ? (
              <div className="mb-2">
                <p className="mb-1.5 text-13 text-txt-secondary">Площадки</p>
                <div className="flex flex-col gap-2.5" role="group" aria-label="Площадки">
                  {sites.map((s) => (
                    <LabeledCheckbox
                      key={s.id}
                      checked={siteIds.includes(s.id)}
                      onCheckedChange={(on) =>
                        setSiteIds((prev) =>
                          on ? [...prev, s.id] : prev.filter((id) => id !== s.id),
                        )
                      }
                    >
                      {s.name}
                    </LabeledCheckbox>
                  ))}
                </div>
                {sitesError ? (
                  <p role="alert" className="mt-1.5 text-12 text-red">
                    {sitesError}
                  </p>
                ) : null}
              </div>
            ) : null}
          </form>
        </Card>
      </DialogContent>
    </Dialog>
  )
}

/** Площадки, по которым пора спросить «что менялось». */
export function ecoChangeCheckDueSites(overview: EcoOverview): EcoSite[] {
  return overview.sites.filter((s) => s.survey.changeCheckDue)
}

export function useEcoChangeCheck(
  api: EcoScopeApi,
  overview: EcoOverview | null,
  onChange: (overview: EcoOverview) => void,
): {
  /** Открыть модалку «Были изменения» (площадки по умолчанию — те, по которым пора спросить, иначе все). */
  openChanges: () => void
  /** «Ничего не менялось» по площадкам, по которым пора спросить. */
  nothingChanged: () => void
  busy: boolean
  /** Итог последнего ответа или ошибка «ничего не менялось». */
  message: ReactNode
  dialog: ReactNode
} {
  const [open, setOpen] = useState(false)
  const [defaultSiteIds, setDefaultSiteIds] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [dialogError, setDialogError] = useState<string | null>(null)
  const [message, setMessage] = useState<ReactNode>(null)

  async function send(input: EcoChangeCheckInput, fromDialog: boolean) {
    setBusy(true)
    setDialogError(null)
    try {
      const res = await api.createChangeCheck(input)
      onChange(res.overview)
      setOpen(false)
      setMessage(
        input.changes.length === 0 ? (
          <Alert variant="info" title="Спасибо, отметили: изменений нет">
            Следующая проверка — через год. Если что-то поменяется раньше, нажмите «Были изменения».
          </Alert>
        ) : res.markedCount > 0 ? (
          <Alert
            variant="info"
            title={`Отмечены изменения: ${res.markedCount} ${pluralRu(res.markedCount, 'документ требует', 'документа требуют', 'документов требуют')} актуализации`}
          >
            Они подсвечены жёлтым «Требует актуализации» в «Проектных работах». Менеджер уточнит
            детали.
          </Alert>
        ) : (
          <Alert variant="info" title="Отмечены изменения">
            Документов к обновлению по этим площадкам нет. Если сомневаетесь, напишите менеджеру —
            он проверит документы.
          </Alert>
        ),
      )
    } catch (err) {
      const text = getErrorMessage(err, 'Не удалось сохранить ответ')
      if (fromDialog) setDialogError(text)
      else setMessage(<Alert variant="danger">{text}</Alert>)
    } finally {
      setBusy(false)
    }
  }

  const due = overview ? ecoChangeCheckDueSites(overview) : []
  return {
    openChanges: () => {
      setDefaultSiteIds((due.length > 0 ? due : (overview?.sites ?? [])).map((s) => s.id))
      setDialogError(null)
      setOpen(true)
    },
    nothingChanged: () => {
      if (due.length > 0) void send({ changes: [], siteIds: due.map((s) => s.id), note: '' }, false)
    },
    busy,
    message,
    dialog: (
      <ChangeCheckDialog
        open={open}
        sites={overview?.sites ?? []}
        defaultSiteIds={defaultSiteIds}
        saving={busy}
        error={dialogError}
        onClose={() => setOpen(false)}
        onSubmit={(input) => void send(input, true)}
      />
    ),
  }
}

/** Плашка ежегодной проверки для площадок, по которым пора спросить «что менялось». */
export function ChangeCheckBanner({
  sites,
  busy,
  onNothingChanged,
  onChanges,
}: {
  sites: EcoSite[]
  busy: boolean
  onNothingChanged: () => void
  onChanges: () => void
}) {
  if (sites.length === 0) return null
  const dates = [...new Set(sites.map((s) => formatEcoDate(s.survey.completedAt.slice(0, 10))))]
  const names = sites.map((s) => `«${s.name}»`).join(', ')
  return (
    <div className="mb-4 flex flex-col gap-3 rounded-12 border border-amber bg-amber-bg px-4 py-3 nw:flex-row nw:items-center">
      <Clock className="hidden size-6 shrink-0 stroke-current text-amber nw:block" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-amber">
          Ежегодная проверка: что менялось на {sites.length > 1 ? 'площадках' : 'площадке'} {names}?
        </p>
        <p className="text-13 text-txt-secondary">
          Опрос пройден {dates.join(', ')}. Если менялись адрес, юрлицо, количество площадок,
          оборудование, техпроцесс, отходы или руководитель — часть документов нужно обновить.
        </p>
      </div>
      <div className="flex shrink-0 flex-wrap gap-2">
        <Button size="sm" variant="secondary" disabled={busy} onClick={onNothingChanged}>
          Ничего не менялось
        </Button>
        <Button size="sm" disabled={busy} onClick={onChanges}>
          Были изменения
        </Button>
      </div>
    </div>
  )
}
